<script setup lang="ts">
import { useClaims } from '@auth/useClaims';
import { useSelectableDocumentTypesGet } from '@services/documenten-public/orval-api/selectable-document-types/selectable-document-types';
import VButton from '@shared/component/VButton/VButton.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VToggleSwitch from '@shared/component/VToggleSwitch/VToggleSwitch.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import StringField from '@shared/form/StringField/StringField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VMultipleChoiceButtons from '@shared/form/VMultipleChoiceButtons/VMultipleChoiceButtons.vue';
import VSelect from '@shared/form/VSelect/VSelect.vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref, watch, watchEffect } from 'vue';
import { z } from 'zod';
import ClassicDialog from '@shared/form/ClassicDialog/ClassicDialog.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import { type SUREbusinessCommonDomainDocumentSoort as DocumentSoort } from '@services/documenten-public/orval-api/model/sUREbusinessCommonDomainDocumentSoort';

export type FileForm = {
  geheim: boolean;
  naam: string;
  soort: string | null | undefined;
  afzender: string;
  toelichting: string;
  file: File;
};

const {
  isActive,
  files,
  selectableDocumentTypes,
  toonAfkomstigePartijVeld = false,
  classicDialog = false,
} = defineProps<{
  isActive: boolean;
  files: File[];
  selectableDocumentTypes?: DocumentSoort[];
  toonAfkomstigePartijVeld?: boolean;
  classicDialog?: boolean;
}>();

const emit = defineEmits<{ close: []; uploadFiles: [files: FileForm[]] }>();

const claims = useClaims();

const { data: documentTypes, isPending: documentTypesPending } = useSelectableDocumentTypesGet(
  {
    documentSoort: selectableDocumentTypes,
  },
  { query: { select: (response) => response.result, enabled: !!selectableDocumentTypes?.length } },
);

const fileIndex = ref(0);
const fileForms = ref<FileForm[]>([]);
const multipleFileFormMode = ref<'inputForAllFiles' | 'inputPerFile'>('inputForAllFiles');
const lockFileFormMode = ref(false);
const multipleFileFormModeOptions = computed(() => [
  {
    value: 'inputForAllFiles',
    label: 'Alles',
    disabled: lockFileFormMode.value,
    description: lockFileFormMode.value
      ? 'Omdat je al meerdere bestanden bewerkt hebt kun je niet meer wisselen. Toch dezelfde gegevens voor alle bestanden gebruiken? sluit het dialoog en begin opnieuw.'
      : 'Wat je hieronder invoert wordt voor alle bestanden gebruikt.',
  },
  {
    value: 'inputPerFile',
    label: 'Per bestand',
    description: 'Voer per bestand het soort document, afkomstige partij en/of toelichting in.',
  },
]);

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      verborgenVoorAdviseur: z.boolean(),
      bestandsnaam: z
        .string()
        .refine(
          (value) => value.trim().length >= 5,
          'De bestandsnaam moet tenminste 5 karakters lang zijn.',
        ),
      soortDocument: z.string({ message: 'Soort document is verplicht.' }),
      afzender: z.string(),
      toelichting: z.string(),
    }),
  ),
);

const {
  defineField,
  errorBag,
  meta: formMeta,
  setValues,
  validate,
} = useForm({
  validationSchema,
  initialValues: {
    verborgenVoorAdviseur: true,
    bestandsnaam: '',
    soortDocument: undefined,
    afzender: '',
    toelichting: '',
  },
});
const [verborgenVoorAdviseur] = defineField('verborgenVoorAdviseur');
const [bestandsnaam] = defineField('bestandsnaam');
const [soortDocument] = defineField('soortDocument');
const [afzender] = defineField('afzender');
const [toelichting] = defineField('toelichting');

function initMultipleFileFormMode() {
  multipleFileFormMode.value = files.length > 1 ? 'inputForAllFiles' : 'inputPerFile';
}

watch(
  () => files,
  () => {
    initMultipleFileFormMode();

    fileForms.value = files.map((file) => ({
      geheim: claims.value.isInternal,
      naam: file.name,
      soort: null,
      afzender: '',
      toelichting: '',
      file,
    }));
    fileIndex.value = 0;
  },
);

watchEffect(() => {
  const fileForm = fileForms.value[fileIndex.value];
  if (!fileForm) return;

  setValues(
    {
      verborgenVoorAdviseur: fileForm.geheim,
      bestandsnaam: fileForm.naam,
      soortDocument: fileForm.soort ?? undefined,
      afzender: fileForm.afzender,
      toelichting: fileForm.toelichting,
    },
    false,
  );
});

async function saveFormIfValid(): Promise<{ valid: boolean }> {
  const validationResult = await validate();
  if (!validationResult.valid) return { valid: false };

  if (multipleFileFormMode.value === 'inputForAllFiles') {
    fileForms.value.forEach((fileForm) => {
      fileForm.geheim = verborgenVoorAdviseur.value ?? true;
      fileForm.soort = soortDocument.value;
      fileForm.afzender = afzender.value || '';
      fileForm.toelichting = toelichting.value || '';
    });
  } else {
    const currentFile = fileForms.value[fileIndex.value];
    currentFile.geheim = verborgenVoorAdviseur.value ?? true;
    currentFile.naam = bestandsnaam.value!;
    currentFile.soort = soortDocument.value;
    currentFile.afzender = afzender.value || '';
    currentFile.toelichting = toelichting.value || '';
  }

  return { valid: true };
}

async function nextFile() {
  lockFileFormMode.value = true;

  const { valid } = await saveFormIfValid();
  if (!valid) return;

  fileIndex.value = Math.min(fileIndex.value + 1, files.length - 1);
}

async function previousFile() {
  const { valid } = await saveFormIfValid();
  if (!valid) return;

  fileIndex.value = Math.max(fileIndex.value - 1, 0);
}

async function uploadFiles() {
  const { valid } = await saveFormIfValid();
  if (!valid) return;

  emit('uploadFiles', fileForms.value);
}

function onClose() {
  multipleFileFormMode.value = 'inputForAllFiles';
  lockFileFormMode.value = false;
  emit('close');
}
</script>

<template>
  <component
    :is="classicDialog ? ClassicDialog : VModal"
    :title="files.length === 1 ? 'Bestand uploaden' : `${files.length} bestanden uploaden`"
    :is-active="isActive"
    @close="onClose()"
  >
    <form class="flex flex-col gap-6" v-if="fileForms.length">
      <FieldRowV2
        v-if="files.length > 1"
        label="Invoermodus"
        tooltip="Geef aan op welke manier je informatie toe wilt voegen aan de bestanden."
      >
        <VMultipleChoiceButtons
          :options="multipleFileFormModeOptions"
          v-model="multipleFileFormMode"
        />
      </FieldRowV2>
      <FieldRowV2
        v-if="claims.isInternal"
        label="Verborgen voor adviseur"
        label-for="verborgenVoorAdviseur"
        :errors="errorBag.verborgenVoorAdviseur"
        tooltip="Of het document alleen intern zichtbaar moet zijn, of ook voor de adviseur zichtbaar."
      >
        <VToggleSwitch id="verborgenVoorAdviseur" v-model:is-active="verborgenVoorAdviseur" />
      </FieldRowV2>
      <FieldRowV2
        required
        label="Bestandsnaam"
        label-for="bestandsnaam"
        :errors="errorBag.bestandsnaam"
        v-if="files.length === 1 || multipleFileFormMode === 'inputPerFile'"
      >
        <StringField id="bestandsnaam" v-model="bestandsnaam" />
      </FieldRowV2>
      <FieldRowV2
        required
        v-if="!!selectableDocumentTypes?.length"
        label="Soort document"
        label-for="soortDocument"
        :errors="errorBag.soortDocument"
      >
        <div class="flex w-full justify-stretch">
          <VSkeleton v-if="documentTypesPending" :count="1" />
          <VSelect
            v-else
            id="soortDocument"
            class="grow"
            :options="documentTypes"
            option-label="type"
            option-value="documentTypeId"
            searchable
            v-model="soortDocument"
          />
        </div>
      </FieldRowV2>
      <FieldRowV2
        v-if="toonAfkomstigePartijVeld"
        label="Afkomstige partij"
        label-for="afkomstigePartij"
        :errors="errorBag.afzender"
      >
        <StringField id="afkomstigePartij" v-model="afzender" />
      </FieldRowV2>
      <FieldRowV2 label="Toelichting" label-for="toelichting" :errors="errorBag.toelichting">
        <TextField :rows="2" :auto-grow="true" id="toelichting" v-model="toelichting" />
      </FieldRowV2>
    </form>

    <template #footer>
      <VTooltip label="Sluit het dialoog. Er wordt niets geüpload.">
        <VButton class="px-3" inverted @click="onClose">Annuleren</VButton>
      </VTooltip>
      <VTooltip
        v-if="multipleFileFormMode === 'inputPerFile' && fileIndex > 0"
        :active="!formMeta.valid"
        label="Los de validatiefouten op om naar het vorige bestand te gaan."
      >
        <VButton inverted :disabled="!formMeta.valid" @click="previousFile">Vorig bestand</VButton>
      </VTooltip>
      <VTooltip
        v-if="multipleFileFormMode === 'inputPerFile' && fileIndex < files.length - 1"
        :active="!formMeta.valid"
        label="Los de validatiefouten op om door te gaan."
      >
        <VButton class="px-3" :disabled="!formMeta.valid" @click="nextFile">
          Volgend bestand ({{ fileIndex + 1 }}/{{ files.length }})
        </VButton>
      </VTooltip>
      <VButton v-else :disabled="!!Object.keys(errorBag).length" @click="uploadFiles">
        Uploaden
      </VButton>
    </template>
  </component>
</template>
